import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import { ChevronDown, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface Option {
  id: string;
  label: string;
  /** 우측 보조 텍스트 (예: 모델명). 검색에는 포함되지 않음. */
  hint?: string;
}

interface SingleSelectFilterProps {
  options: Option[];
  /** null = 선택 없음. */
  value: string | null;
  onChange: (id: string | null) => void;
  /** 선택 없음일 때 트리거에 표시. */
  triggerPlaceholder: string;
  /** 검색 input placeholder. */
  searchPlaceholder?: string;
  /** 옵션 수가 적으면 false 로 검색 input 숨김. 기본 true. */
  searchable?: boolean;
  /** X 클릭으로 선택 해제 가능. 기본 true. */
  clearable?: boolean;
  /** 트리거 클래스 — 기본 h-8 text-xs. h-9 등으로 키울 때 사용. */
  triggerClassName?: string;
  disabled?: boolean;
}

/**
 * UsageStats / ChatLog 의 MultiSelectFilter 와 동일 디자인 톤의 단일 선택 버전.
 * - 트리거 클릭 → fixed-position 드롭다운 (트리거 아래)
 * - 옵션 클릭 즉시 close
 * - X 로 선택 해제 (clearable)
 * - native <select> 의 디폴트 스타일 회피 + 검색 가능
 */
export function SingleSelectFilter({
  options,
  value,
  onChange,
  triggerPlaceholder,
  searchPlaceholder,
  searchable = true,
  clearable = true,
  triggerClassName,
  disabled = false,
}: SingleSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [rect, setRect] = useState<DOMRect | null>(null);
  /** 'down' = 트리거 아래로, 'up' = 위로. 트리거 위치 기준 자동 결정. */
  const [placement, setPlacement] = useState<'down' | 'up'>('down');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /** 드롭다운 추정 높이 (검색 input + max-h-48 = ~232px). */
  const ESTIMATED_DROPDOWN_HEIGHT = 240;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !dropdownRef.current?.contains(t)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // 닫힐 때 검색어 초기화.
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const filtered = useMemo(
    () =>
      search
        ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
        : options,
    [search, options],
  );

  const selectedOption = options.find((o) => o.id === value) ?? null;

  return (
    // min-w-0 — flex 부모 안에서 트리거가 컨텐츠 길이에 따라 부풀지 않도록.
    // 트리거 내부 span 의 truncate 가 실제로 동작하려면 이 가드가 필요.
    <div className="relative min-w-0">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          if (!open && triggerRef.current) {
            const r = triggerRef.current.getBoundingClientRect();
            setRect(r);
            // 아래 여백이 부족하고 위가 더 넓으면 위로 펼침.
            const spaceBelow = window.innerHeight - r.bottom;
            const spaceAbove = r.top;
            setPlacement(
              spaceBelow < ESTIMATED_DROPDOWN_HEIGHT && spaceAbove > spaceBelow ? 'up' : 'down',
            );
          }
          setOpen((v) => !v);
        }}
        className={cn(
          'flex items-center gap-1 w-full h-8 rounded-md border border-border bg-surface px-2 text-xs hover:bg-surface-muted transition-colors cursor-pointer',
          selectedOption ? 'text-foreground' : 'text-foreground-muted',
          disabled ? 'cursor-not-allowed opacity-50 hover:bg-surface' : '',
          triggerClassName,
        )}
      >
        {/* 트리거에는 label 만 표시 — hint 는 dropdown option 에서만 보임 (트리거 폭 부풀림 회피). */}
        <span className="truncate flex-1 text-left">
          {selectedOption ? selectedOption.label : triggerPlaceholder}
        </span>
        {clearable && selectedOption ? (
          // 트리거 button 안에 nested button 은 invalid HTML → span + role="button".
          // biome-ignore lint/a11y/useSemanticElements: nested button 회피
          <span
            role="button"
            tabIndex={0}
            aria-label="선택 해제"
            title="선택 해제"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onChange(null);
              }
            }}
            className="shrink-0 p-0.5 rounded hover:bg-foreground/10 text-foreground-muted hover:text-foreground cursor-pointer"
          >
            <X className="size-3" />
          </span>
        ) : null}
        <ChevronDown
          className={cn(
            'size-3.5 shrink-0 text-foreground-muted transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && rect ? (
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            ...(placement === 'up'
              ? { bottom: window.innerHeight - rect.top + 4 }
              : { top: rect.bottom + 4 }),
            left: rect.left,
            width: rect.width,
            zIndex: 9999,
          }}
          className="rounded-md border border-border bg-surface shadow-lg"
        >
          {searchable ? (
            <div className="p-2 border-b border-border">
              <Input
                placeholder={searchPlaceholder ?? '검색…'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-7 text-xs"
                autoFocus
              />
            </div>
          ) : null}
          <div className="max-h-48 overflow-y-auto divide-y divide-border">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-xs text-foreground-muted">검색 결과 없음</p>
            ) : (
              filtered.map((o) => {
                const isSelected = o.id === value;
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => {
                      onChange(o.id);
                      setOpen(false);
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-surface-muted text-left',
                      isSelected ? 'bg-accent/10 text-accent' : 'text-foreground',
                    )}
                  >
                    <span className="truncate flex-1">{o.label}</span>
                    {o.hint ? (
                      <span className="shrink-0 text-xs text-foreground-muted">{o.hint}</span>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
